import React from "react";
import { Card, Flex, Text, LoadingIndicator } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import * as styles from "./styles.css";
import { LogsListProps } from "./types";
import { formatDateTime } from "./utils";

/**
 * Generic, reusable LogsList component that can be configured for different log types
 * (e.g., PayeeLog, PaymentBatchLog, etc.)
 *
 * @template TLog - The type of log entry
 */
export function LogsList<TLog extends { id: string; dateTime: string }>({
  logs,
  loading,
  error: loadingError,
  config,
}: LogsListProps<TLog>) {
  if (loading) {
    return (
      <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={Sizes.MD}>
        <LoadingIndicator />
        <Text variant="regular-md" color="muted">
          Loading activity logs...
        </Text>
      </Flex>
    );
  }

  if (loadingError) {
    return (
      <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={Sizes.MD}>
        <Text variant="regular-md" color="error">
          Error loading activity logs: {loadingError.message}
        </Text>
      </Flex>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={Sizes.MD}>
        <Text variant="regular-md" color="muted">
          No activity logs found
        </Text>
      </Flex>
    );
  }

  return (
    <Card padding="lg">
      <Text className={config.cardTitleClassName} variant="secondary-md">
        {config.cardTitle || "LOG"}
      </Text>
      <Flex flexDirection="column" gap={Sizes.MD}>
        {logs.map((logEntry, index) => {
          const isLastEntry = index === logs.length - 1;
          return (
            <Flex
              key={logEntry.id}
              flexDirection="column"
              gap={8}
              className={isLastEntry ? styles.logEntryWrapper : styles.logEntryWrapperWithLine}
            >
              <Flex alignItems="flex-start" gap={12}>
                <div className={styles.iconContainer}>
                  <Icon
                    type={config.getIcon(logEntry)}
                    size="medium"
                    customColor={Colors.Neutral900}
                  />
                </div>
                <Flex flexDirection="column" gap={Sizes.XS} className={styles.logEntryContent}>
                  <Text variant="regular-md">{config.formatActionDescription(logEntry)}</Text>
                  <Text variant="secondary-sm" color="muted">
                    {formatDateTime(logEntry.dateTime)}
                  </Text>
                  {config.shouldShowReason(logEntry) && config.getReason(logEntry) && (
                    <div className={config.reasonBoxClassName}>
                      <Text variant="regular-sm">{config.getReason(logEntry)}</Text>
                    </div>
                  )}
                </Flex>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Card>
  );
}
